import {
  Alert,
  Button,
  Card,
  Col,
  Row,
  Statistic,
  Tooltip,
  Typography,
} from 'antd';
import { ArrowUpOutlined, ReloadOutlined } from '@ant-design/icons';
import { get } from 'lodash';
import React, { useState } from 'react';
import { useQuery } from 'react-apollo';
import { Link } from 'react-router-dom';
import {
  ADMIN_INVESTMENT_WALLET,
  ADMIN_TREASURY,
} from '../../pages/apollo_queries';
import { MovementModal } from './MovementModal';

// The note's own Sila wallet, LIVE, and the one thing an operator does with it: send its money to a
// bank of the house so it can be lent (the legacy note page's "Withdraw", 2026-09-18). The banks are
// the treasury's — the note's wallet hangs under the house's handle — so they come from the treasury
// read; the movement itself lands in the treasury's movements table with this note in its Note column.
export function NoteWalletCard({ investmentId, noteName }) {
  const [modal, setModal] = useState(false);
  const { data, loading, error, refetch } = useQuery(ADMIN_INVESTMENT_WALLET, {
    variables: { investmentId },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });
  // Only the banks are needed here; the smallest page keeps the read cheap.
  const { data: treasuryData, loading: treasuryLoading } = useQuery(
    ADMIN_TREASURY,
    {
      variables: { page: 1, count: 1 },
      fetchPolicy: 'network-only',
    }
  );

  const wallet = get(data, 'adminInvestmentWallet', null);
  const banks = get(treasuryData, 'adminTreasury.banks', null);
  const canWithdraw = !!wallet && Array.isArray(banks) && banks.length > 0;
  const available = wallet ? wallet.availableBalance : null;

  return (
    <Card
      title="Note wallet"
      loading={loading && !data}
      extra={
        <>
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={() => refetch()}
            loading={loading}
            style={{ marginRight: 8 }}
          >
            Refresh
          </Button>
          <Tooltip
            title={
              canWithdraw
                ? ''
                : 'Needs the note wallet and at least one bank linked to the treasury'
            }
          >
            <Button
              danger
              size="small"
              icon={<ArrowUpOutlined />}
              disabled={!canWithdraw || treasuryLoading}
              onClick={() => setModal(true)}
            >
              Withdraw to bank
            </Button>
          </Tooltip>
        </>
      }
    >
      {error ? (
        <Alert
          type="error"
          showIcon
          message="The note wallet could not be read"
          description={get(error, 'graphQLErrors[0].message') || error.message}
        />
      ) : wallet ? (
        <Row gutter={24}>
          <Col span={10}>
            <Statistic
              title="Available"
              value={
                wallet.availableBalance == null ? '—' : wallet.availableBalance
              }
              precision={2}
              prefix="$"
              valueStyle={{ fontSize: 28, fontWeight: 600 }}
            />
          </Col>
          <Col span={7}>
            <Statistic
              title="Balance"
              value={wallet.balance == null ? '—' : wallet.balance}
              precision={2}
              prefix="$"
            />
          </Col>
          <Col span={7}>
            <Statistic
              title="Pending"
              value={
                wallet.pendingBalance == null ? '—' : wallet.pendingBalance
              }
              precision={2}
              prefix="$"
            />
          </Col>
        </Row>
      ) : (
        <Alert
          type="warning"
          showIcon
          message="This note has no wallet yet, or Sila could not be reached"
          description="A wallet is provisioned the first time an investor's money reaches the note."
        />
      )}
      <Typography.Paragraph
        type="secondary"
        style={{ marginTop: 16, marginBottom: 0 }}
      >
        Money withdrawn here goes to a bank of Constitution and shows up in the{' '}
        <Link to="/treasury">Treasury</Link> movements with this note in its
        Note column.
      </Typography.Paragraph>

      {modal && (
        <MovementModal
          direction="noteWithdraw"
          banks={banks || []}
          extra={{ investmentId }}
          available={available}
          sourceLabel={noteName}
          onClose={() => setModal(false)}
          onDone={refetch}
        />
      )}
    </Card>
  );
}
